import React, { useContext, useEffect, useState } from 'react'
import { Marker } from 'react-map-gl'
import classnames from 'classnames'
import get from 'lodash/get'

import { Banner, Button, DeemphasizedLink, Text } from '@fielded/shared-ui'
import { MapMarker, QuestionMark, Truck } from '@fielded/shared-ui/src/icons'

import MapContainer from '../../../../../../../../van-shared/components/MapContainer/MapContainer'
import { AuthenticationContext } from '../PSMDeliveryAuthenticationContainer'
import { GPS_ACCURACY_WARN_THRESHOLD_M, isWithinDeliveryRadius } from '../../../utils'

const getMapZoom = (distance) => {
  if (distance == null) return 12
  if (distance <= 2) return 15
  if (distance <= 10) return 12
  if (distance <= 50) return 9
  return 7
}

export const getProximityMessage = ({
  isWithinLocationRadius,
  distance,
  facilityName
}) => {
  if (isWithinLocationRadius) {
    return <Text>You're just <Text tag='span' bold>{distance}km</Text> away from <Text tag='span' bold>{facilityName}</Text>, well within our delivery radius!</Text>
  }
  return <Text>You are located <Text tag='span' bold>{distance}km</Text> away from <Text tag='span' bold>{facilityName}</Text>, which is outside the facility's delivery radius. Please head to the facility to deliver the package.</Text>
}

const PSMDeliveryMap = () => {
  const {
    distance,
    facilityName,
    isDeniedPermission,
    hasNoCoordinates,
    geoPosition,
    position,
    locationObject,
    onRetryGeolocation,
    isRetryingGps,
    hasAcceptedLowAccuracy,
    onAcceptLowAccuracy,
    isLocating,
    hasGpsTimeout
  } = useContext(AuthenticationContext)

  const isWithinLocationRadius = isWithinDeliveryRadius(distance)

  // Denied permission takes priority over hasNoCoordinates: until the
  // driver re-enables permission they can't act on anything else, and
  // the verify CTA stays disabled - show instructions to recover.
  if (isDeniedPermission) {
    return (
      <section className='delivery-map'>
        <section className={classnames(
          'vs-u-align-center',
          'delivery-map__content',
          'delivery-map__content--no-coordinates'
        )}>
          <Text tag='span' className='delivery-map__content-divider-bg'>
            {'-'.repeat(100)}
          </Text>

          <Text className='delivery-map__content-icon'>
            <Truck direction='right' />
          </Text>

          <Text className='delivery-map__content-icon'>
            <QuestionMark />
          </Text>
        </section>

        <Banner type='info' inline center={false}>
          <Text>
            Oops! It looks like you haven't granted location permission. Please navigate to your <Text tag='span' bold>browser settings</Text> to grant permission, then refresh this page to continue.
          </Text>
        </Banner>
      </section>
    )
  }

  if (hasNoCoordinates) {
    return (
      <section className='delivery-map'>
        <section className={classnames(
          'vs-u-align-center',
          'delivery-map__content',
          'delivery-map__content--no-coordinates'
        )}>
          <Text tag='span' className='delivery-map__content-divider-bg'>
            {'-'.repeat(100)}
          </Text>

          <Text className='delivery-map__content-icon'>
            <Truck direction='right' />
          </Text>

          <Text className='delivery-map__content-icon'>
            <QuestionMark />
          </Text>
        </section>

        <Banner type='info' inline center={false}>
          <Text>
            We're unable to verify your distance from <Text tag='span' bold>{facilityName}</Text>. Please ensure you are at the facility before proceeding.
          </Text>
        </Banner>
      </section>
    )
  }

  const accuracy = get(geoPosition, 'coords.accuracy')

  const hasLowAccuracy = !isDeniedPermission &&
    accuracy != null &&
    accuracy > GPS_ACCURACY_WARN_THRESHOLD_M &&
    !hasAcceptedLowAccuracy

  // Parse facility coordinates from the location document
  const facilityLatLng = get(locationObject, 'additionalData.latLng', '')
  const facilityParts = facilityLatLng.split(',')
  const facilityLat = parseFloat(facilityParts[0])
  const facilityLng = parseFloat(facilityParts[1])
  const hasFacilityCoords = facilityLatLng !== 'null,null' && !isNaN(facilityLat) && !isNaN(facilityLng)
  const hasDriverCoords = position.lat != null && position.lng != null

  const initialLat = hasFacilityCoords ? facilityLat : 9.59396
  const initialLng = hasFacilityCoords ? facilityLng : 8.105306

  const [mapViewport, setMapViewport] = useState({
    latitude: initialLat,
    longitude: initialLng,
    zoom: getMapZoom(distance)
  })

  useEffect(() => {
    if (distance != null) {
      setMapViewport(prev => ({ ...prev, zoom: getMapZoom(distance) }))
    }
  }, [distance])

  let proximityMessage = ''
  if (isLocating && !hasGpsTimeout) {
    proximityMessage = <Text>Locating you, please wait...</Text>
  } else if (isLocating && hasGpsTimeout) {
    proximityMessage = <Text>Unable to determine your location. You can try again or continue with delivery.</Text>
  } else {
    proximityMessage = getProximityMessage({
      distance: Math.ceil(distance),
      isWithinLocationRadius,
      facilityName
    })
  }

  return (
    <section className='delivery-map'>
      {(hasFacilityCoords || hasDriverCoords) && (
        <div className='delivery-map__map-view'>
          <MapContainer
            latitude={mapViewport.latitude}
            longitude={mapViewport.longitude}
            zoom={mapViewport.zoom}
            style={{width: '100%', height: 220}}
            attributionControl={false}
            onMove={(e) => setMapViewport(e.viewState)}
            onMapLoad={() => {}}
            onClick={() => {}}
          >
            {hasFacilityCoords && (
              <Marker latitude={facilityLat} longitude={facilityLng} anchor='bottom'>
                <Text className='delivery-map__map-marker delivery-map__map-marker--facility'>
                  <MapMarker />
                </Text>
              </Marker>
            )}
            {hasDriverCoords && (
              <Marker latitude={position.lat} longitude={position.lng}>
                <Text className='delivery-map__map-marker delivery-map__map-marker--driver'>
                  <Truck direction='right' />
                </Text>
              </Marker>
            )}
          </MapContainer>
        </div>
      )}

      <Text className='vs-u-margin-top'>{proximityMessage}</Text>

      {hasLowAccuracy && (
        <Banner
          className='vs-u-margin-top'
          title='Low GPS accuracy'
          type='warning'
          inline
          tight
        >
          <Text>Your GPS accuracy is <Text tag='span' bold>{Math.round(accuracy)}m</Text>. Your detected location may not be accurate.</Text>
          <section className='delivery-map__accuracy-actions vs-u-margin-top'>
            <Button
              fill='outline'
              colorVariant='warning'
              size='small'
              loading={isRetryingGps}
              onClick={onRetryGeolocation}
            >
              Try again
            </Button>
            <DeemphasizedLink
              colorVariant='brand'
              onClick={onAcceptLowAccuracy}
            >
              Continue anyway
            </DeemphasizedLink>
          </section>
        </Banner>
      )}
    </section>
  )
}

export default PSMDeliveryMap
